import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, enableProdMode, isDevMode, signal } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { CoreEffects } from '@next/core-lib';
import { provideAgrCore } from '@next/core-lib/app';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { I18nEffects } from '@next/core-lib/i18n';
import { provideAgrNavigation, NavigationEffects } from '@next/core-lib/navigation';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { provideAgrSite, SiteEffects } from '@next/core-lib/site';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { AppEffects } from './app/shared/store/effects';
import { reducer } from './app/shared/store/reducers';
import { setEnvironment } from './environment';
import { environment } from './environments/environment';

if (environment.PRODUCTION) {
  enableProdMode();
}

const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  try {
    const response = await globalThis.fetch('/assets/config.json');
    if (response.ok) {
      const config = await response.json();
      if (Object.keys(config).length > 0) {
        loadedConfig.set({ ...environment, ...setEnvironment(config) });
      }
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

setupRuntimeConfig.then(() => {
  bootstrapApplication(AppComponent, {
    providers: [
      provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
      provideHttpClient(withInterceptorsFromDi()),
      provideStore(
        {},
        {
          runtimeChecks: {
            strictStateImmutability: true,
            strictActionImmutability: true,
            strictStateSerializability: false,
            strictActionSerializability: false,
          },
        },
      ),
      provideState('app', reducer),
      provideAgrRouterStore(),
      provideAgrCore(),
      provideAgrBrowser({ enableServiceWorker: environment.PRODUCTION }),
      provideAgrSite(),
      provideAgrNavigation(),
      provideAgrElasticApi(),
      provideAgrSettings(),
      provideEffects([
        AppEffects,
        CoreEffects,
        I18nEffects,
        I18nBrowserEffects,
        SiteEffects,
        NavigationEffects,
      ]),
      provideStoreDevtools({
        maxAge: 25,
        logOnly: !isDevMode(),
        autoPause: true,
        trace: false,
        traceLimit: 75,
        connectInZone: false,
      }),
      {
        provide: ENVIRONMENT_MAPPER,
        useFactory: () => loadedConfig(),
      },
      {
        provide: APP_ID,
        useFactory: () => loadedConfig().APPLICATION_NAME,
      },
    ],
  }).catch((err) => console.error(err));
});
